/**
 * Cart table
 * ------------------------------------------------------------------------------
 * An event based Ajax cart table
 *
 * @namespace cartTable
 */
import axios from 'axios';
import Cookies from 'js-cookie';
import {formatMoney} from '@shopify/theme-currency';
import * as cart from '@shopify/theme-cart';

import {on, extendDefaults} from '../helpers/utils';
import cssClasses from '../helpers/cssClasses';
import {misc as icons} from '../helpers/svg-map';
import QuantitySelector from './quantity-selector';
import FreeShippingNotification from './free-shipping-notification';

/**
 * DOM selectors.
 */
const selectors = {
  ajaxCart: 'js-ajax-cart',
  lineItem: '[js-cart-table="lineItem"]',
  lineItemPrice: '[js-cart-table="lineItemPrice"]',
  quantitySelector: '[js-cart-table="quantitySelector"]',
  subtotal: '[js-cart-table="subtotal"]',
  total: '[js-cart-table="total"]',
  totalDiscountContainer: '[js-cart-table="totalDiscountContainer"]',
  totalDiscountPrice: '[js-cart-table="totalDiscountPrice"]',
};

/**
 * Create new cart table object.
 */
export default (selector, config) => {

  /**
   * Instance default settings.
   */
  const defaults = {
    hideAddOns: true,
  };

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selector),
    subtotal: document.querySelectorAll(selectors.subtotal),
    total: document.querySelector(selectors.total),
    totalDiscountContainer: document.querySelector(selectors.totalDiscountContainer),
    totalDiscountPrice: document.querySelector(selectors.totalDiscountPrice),
  };

  /**
   * Instance settings.
   */
  const settings = extendDefaults(defaults, config);

  /**
   * Instance globals.
   */
  let timer = {};

  /**
   * Initialise component.
   */
  function init() {
    render();
    setEventBusListeners();
    setEventListeners(nodeSelectors.container);
  }

  /**
   * Set listeners.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen(['AjaxCart:itemRemoved', 'AjaxCart:giftAdded'], () => render());
    Frame.EventBus.listen(['AjaxCart:removingItem'], (item) => setLoadingState(item));
    Frame.EventBus.listen(['CartTable:updatePrices'], (response) => updateCartPrices(response));


    if (theme.features.cartAddOn) {
      Frame.EventBus.listen([
        'AjaxCart:addOnItemAdded',
        'AjaxCart:addOnItemRemoved',
      ], () => render());
    }
  }

  /**
   * Render cart template.
   */
  function render() {
    Frame.EventBus.emit('Cart:updatingPrices');

    axios.get('/cart.js')
      .then((response) => {
        nodeSelectors.container.innerHTML = getCartTemplate(response.data);
        constructQuantitySelectors();
        updateCartPrices(response.data);

        if (theme.features.currencyConverter) {
          Frame.MultiCurrency.CurrencyConverter.convertAll(Cookies.get('new_currency'));
        }

        Frame.EventBus.emit('Cart:pricesUpdated', response.data);
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  /**
  * Get cart template
  * @param {object} data - Cart data.
  */
  function getCartTemplate(data) {
    const lineItemsTemplate = data.items.map((item, index) => {
      if (isAddOnItem(item)) {
        return '';
      }

      return getLineItemTemplate(item, index);
    }).join('');

    return `
      <div class="cart-table__body ajax-cart">
        ${lineItemsTemplate}
      </div>
    `;
  }

  /**
  * Get line item template.
  * @param {object} item - Line item data object.
  */
  function getLineItemTemplate(item, index) {
    return `
      <div
        class="cart-table__item ajax-cart__line-item row no-margin"
        data-key="${item.key}"
        data-index="${index}"
        js-cart-table="lineItem"
      >
        <div class="cart-table__image ajax-cart__image xs3 col">
          <a href="${item.url}">
            <img src="${item.image}" alt="${item.title}">
          </a>
        </div>

        <div class="cart-table__description xs6 col">
          ${isVendorEnabled() ? getVendorTitle(item) : ''}

          <a href="${item.url}" class="ajax-cart__title cart-table__title h6">
            ${item.product_title}

            ${getPreorderBadge(item)}
          </a>

          ${getTagline(item)}

          ${getVariantTitle(item)}

          <button
            class="ajax-cart__remove cart-table__remove text-link"
            data-cy="removeProduct"
            data-key="${item.key}"
            data-variant="${item.variant_id}"
            js-ajax-cart="remove"
          >
            ${theme.strings.cart.general.remove}
          </button>

          ${getPreorderMessage(item)}
        </div>

        <div
          class="ajax-cart__price cart-table__price xs3 col h5"
          js-cart-table="lineItemPrice"
        >
          ${getLineItemPrice(item)}
        </div>

        <div class="ajax-cart__amount cart-table__amount xs3 col">
          ${getQuantitySelectorTemplate(item, index)}
        </div>
      </div>
    `;
  }

  /**
   * Check if current item is an add-on.
   * @param {object} item - Cart variant item.
   */
  function isAddOnItem(item) {
    if (!item) {
      throw Error('Could not find item');
    }

    return (settings.hideAddOns && item.product_type.toLowerCase() === 'add-on');
  }

  /**
   * Check if vendor title setting is enabled.
   */
  function isVendorEnabled() {
    return JSON.parse(nodeSelectors.container.dataset.enableVendorTitle);
  }

  /**
   * Get quantity selector template.
   * @param {object} item - Cart item to get properties from.
   * @param {integer} index - Unique index to bind component to.
   */
  function getQuantitySelectorTemplate(item, index) {
    return `
    <div class="cart-table__quantity-selector" data-label="Quantity">
      <label for="Quantity-${index}" class="label visually-hidden">Quantity</label>

      <div
        id="QuantitySelector-${index}"
        class="quantity-selector quantity-selector--small"
        data-max="${item.properties._max_inventory}"
        js-cart-table="quantitySelector"
      >
        <div class="quantity-selector__control">
          <button
            class="button quantity-selector__button"
            value="decrement"
            js-quantity-selector="decrement"
          >
            <span class="visually-hidden">-</span>
            ${icons.minus}
          </button>
        </div>

        <div class="quantity-selector__control">
          <input
            id="Quantity-${index}"
            class="quantity-selector__input"
            min="1"
            name="quantity"
            pattern="[0-9]*"
            readonly
            type="number"
            value="${item.quantity}"
            js-quantity-selector="input"
          />
        </div>

        <div class="quantity-selector__control">
          <button
            class="button quantity-selector__button"
            value="increment"
            data-cy="quantityIncrease"
            js-quantity-selector="increment"
          >
            <span class="visually-hidden">+</span>
            ${icons.plus}
          </button>
        </div>
      </div>
    </div>
    `;
  }

  /**
   * Get vendor title template.
   * @param {object} item - Cart item to get vendor title from.
   */
  function getVendorTitle(item) {
    return `<p class="ajax-cart__vendor meta">${item.vendor}</p>`;
  }


  /**
   * Get tagline with model and colour.
   * @param {object} item - Cart item to get vendor title from.
   */
  function getTagline(item) {
    return `<p class="ajax-cart__tagline label">${item.properties._tagline}</p>`;
  }


  /**
   * Get Preorder badge.
   * @param {object} item - Line item data object.
   */
  function getPreorderBadge(item) {
    return (item.properties._preorder)
      ? `<div class="cart-table__badge badge badge--pre-order">
          <span>
            ${theme.strings.preOrder}
          </span>
        </div>`
      : '';
  }

  function getPreorderMessage(item) {
    return (item.properties._preorder)
      ? `<div class="label cart-table__pre-order-label">
          <span>
            ${theme.strings.cart.general.preOrderNote}
          </span>
        </div>`
      : '';
  }


  /**
   * Get variant titles.
   * @param {object} item - Line item data object.
   */
  function getVariantTitle(item) {
    let optionsString = '';

    if (item.options_with_values.length) {
      Object.keys(item.options_with_values).forEach((index) => {
        const option = item.options_with_values[index];
        optionsString += (option.value.includes('Default Title') || option.name.includes('colour'))
          ? ''
          : `<p class="ajax-cart__variant-title label">${option.name}: ${option.value}</p>`;
      });
    }

    return optionsString;
  }

  /**
   * Get formatted line item price.
   * @param {object} item - Line item data object.
   */
  function getLineItemPrice(item) {
    if (item.discounted_price !== item.price) {
      return getDiscountedPriceTemplate(item.discounted_price, item.price);
    }

    if (item.original_line_price !== item.line_price) {
      return getDiscountedPriceTemplate(item.line_price, item.original_price);
    }

    // if (item.properties._discounted) {
    //   return getDiscountedPriceTemplate(item.line_price, item.properties._discounted);
    // }

    return `
      <span js-currency="price" data-price="${item.price}">
        ${formatMoney(item.price, theme.moneyFormat)}
      </span>
    `;
  }

  /**
   * Returns the discount line price template.
   * @param {Integer} discounted - The discount price.
   * @param {Integer} price - The original price.
   */
  function getDiscountedPriceTemplate(discounted, price) {
    return `
      <span class="visually-hidden">{{ 'cart.label.discounted_price' | t }}</span>

      <span class="h5" js-currency="price" data-price="{{ item.price }}">
        ${formatMoney(discounted, theme.moneyFormat)}
      </span>
    
      <span class="visually-hidden">{{ 'cart.label.original_price' | t }}</span>
      <s>${formatMoney(price, theme.moneyFormat)}</s>
    `;
  }

  /**
   * Set event delegation to remove items on container.
   * @param {HTMLElement} container - The container to apply the remove events on.
   */
  function setEventListeners(container) {
    on('click', container, (event) => handleRemoveEvent(event));
  }

  /**
   * Handle remove events.
   * @param {Event} event - Click event.
   */
  function handleRemoveEvent(event) {
    if (!isTargetRemove(event.target)) {
      return;
    }

    event.preventDefault();
    Frame.EventBus.emit('AjaxCart:removingItem', event.target.closest(selectors.lineItem));

    cart.removeItem(event.target.dataset.key)
      .then((response) => {
        Frame.EventBus.emit('AjaxCart:itemRemoved', response);
        Frame.EventBus.emit('AjaxCart:variantRemoved', Number(event.target.dataset.variant));
        return response;
      })
      .catch((error) => {
        window.location.reload();
        throw new Error('Could not remove item from cart', error);
      });
  }

  /**
   * Detect remove target click.
   * @param {HTMLElement} target - Clicked target to check remove attributes against.
   */
  function isTargetRemove(target) {
    return (
      typeof target.attributes[selectors.ajaxCart] !== 'undefined' &&
      target.attributes[selectors.ajaxCart].nodeValue === 'remove'
    );
  }

  /**
   * Instantiate quantity selectors using id.
   * - Callback has a timer to reduce number of calls.
   */
  function constructQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll(selectors.quantitySelector);

    [...quantitySelectors].forEach((item) => {
      QuantitySelector(`${item.id}`, () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          handleQuantityEvent(item);
        }, 250);
      }).init();

    });
  }

  /**
   * Handle quantity updates.
   * @param {HTMLElement} element - Quantity selector node element.
   */
  function handleQuantityEvent(element) {
    Frame.EventBus.emit('Cart:updatingPrices');

    const data = {
      index: element.closest(selectors.lineItem).dataset.index,
      key: element.closest(selectors.lineItem).dataset.key,
      quantity: parseInt(element.querySelector('input').value, 10),
    };

    cart.updateItem(data.key, {quantity: data.quantity})
      .then((response) => {
        updateCartPrices(response);
        updateLineItem(response);

        if (theme.features.freeShippingNotification) {
          FreeShippingNotification().update(response.total_price);
        }

        Frame.EventBus.emit('Cart:pricesUpdated');
        Frame.EventBus.emit('AjaxCart:itemUpdated');
      })
      .catch((error) => {
        if (typeof response === 'undefined') {
          return;
        }

        throw new Error('Could not update item data', error);
      });
  }

  /**
   * Update subtotal and discount total.
   * @param {object} data - Cart response data.
   */
  function updateCartPrices(data) {
    updateSubtotal(data.items_subtotal_price);
    updateDiscountTotal(data.total_discount);
    updateTotal(data.total_price);
  }

  /**
   * Update line item price based on key and response.
   * @param {object} response - Cart response after line item update.
   */
  function updateLineItem(response) {
    const lineItems = document.querySelectorAll(`${selectors.lineItem}`);

    [...lineItems].forEach((lineItem) => {
      const priceSelector = lineItem.querySelector(selectors.lineItemPrice);
      const variant = response.items.find((item, index) => {
        return Number(lineItem.dataset.index) === Number(index);
      });

      lineItem.setAttribute('data-key', variant.key);
      priceSelector.innerHTML = getLineItemPrice(variant);
    });
  }

  /**
  * Update subtotal.
  * @param {Number} price - Total subtotal price number.
  */
  function updateSubtotal(price) {
    [...nodeSelectors.subtotal].forEach((element) => {
      element.setAttribute('data-price', price);
      element.innerHTML = formatMoney(price, theme.moneyFormat);
    });
  }

  /**
  * Update total.
  * @param {Number} price - Total cart price number.
  */
  function updateTotal(price) {
    nodeSelectors.total.setAttribute('data-price', price);
    nodeSelectors.total.innerHTML = formatMoney(price, theme.moneyFormat);
  }

  /**
   * Update discount total.
   * @param {Number} price - Total discount price number.
   */
  function updateDiscountTotal(price) {
    if (!isCartDiscounted(price)) {
      hideDiscountLabel();
      return;
    }
    showDiscountLabel();
    nodeSelectors.totalDiscountPrice.setAttribute('data-price', price);
    nodeSelectors.totalDiscountPrice.innerHTML = formatMoney(price, theme.moneyFormat);
  }

  /**
   * Set loading state.
   * @param {object} element - Line item row in DOM.
   */
  function setLoadingState(element) {
    Frame.EventBus.emit('Cart:updatingPrices');
    element.classList.add(cssClasses.isRemoving);
  }

  /**
   * Check discount total.
   * @param {Number} total - Total discount price number.
   */
  function isCartDiscounted(total) {
    return total > 0;
  }

  /**
   * Hide discount label.
   */
  function hideDiscountLabel() {
    nodeSelectors.totalDiscountContainer.classList.add(cssClasses.hidden);
  }

  /**
   * Show discount label.
   */
  function showDiscountLabel() {
    nodeSelectors.totalDiscountContainer.classList.remove(cssClasses.hidden);
  }

  return Object.freeze({
    init,
  });
};
